import { useState, useEffect } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { parseEntityRef } from '@backstage/catalog-model';
import { ComponentInfoData } from './types';

// Helper function to format repository name into a proper title
const formatRepositoryTitle = (name: string): string => {
  return name
    .replace(/[._-]/g, ' ') // Replace dots, underscores, and dashes with spaces
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

export const useComponentInfoData = (
  repositoryValue: string,
): {
  data: ComponentInfoData | null;
  loading: boolean;
  error: string | null;
} => {
  const catalogApi = useApi(catalogApiRef);
  const [data, setData] = useState<ComponentInfoData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isCancelled = false;

    const fetchComponentInfo = async () => {
      if (!repositoryValue) {
        if (!isCancelled) {
          setData(null);
        }
        return;
      }

      try {
        if (!isCancelled) {
          setLoading(true);
          setError(null);
        }

        // Parse the entity reference to get the entity
        const entityRef = parseEntityRef(repositoryValue);
        const { items } = await catalogApi.getEntities({
          filter: {
            kind: entityRef.kind || 'Repository',
            'metadata.namespace': entityRef.namespace,
            'metadata.name': entityRef.name,
          },
        });

        if (isCancelled) {
          return;
        }

        if (items.length > 0) {
          const entity = items[0];
          const spec = entity.spec as any;

          // Use spec.fullName.split('/')[1] for component name, fallback to metadata.name
          const componentName = spec?.fullName
            ? String(spec.fullName).split('/')[1]
            : entity.metadata.name;

          const componentInfo: ComponentInfoData = {
            componentName,
            componentTitle: formatRepositoryTitle(componentName),
            componentDescription:
              entity.metadata.description || 'No description available',
            // spec.fullName typically contains the repository full name like 'org/repo'
            githubRepository: spec?.fullName,
            tenant: spec?.system,
            owner: spec?.owner,
          };

          setData(componentInfo);
        } else {
          setData(null);
          setError('Repository not found');
        }
      } catch (err) {
        if (!isCancelled) {
          setError(
            err instanceof Error
              ? err.message
              : 'Failed to fetch component information',
          );
          setData(null);
        }
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };

    fetchComponentInfo();

    // Cleanup function to cancel pending requests
    return () => {
      isCancelled = true;
    };
  }, [repositoryValue, catalogApi]);

  return { data, loading, error };
};
